import { useState, useEffect, useMemo } from 'react';

import { LockOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Input,
  message,
  Modal,
  Select,
  Tag,
  Tooltip,
} from 'antd';

import { createTagRequest } from '../../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { ROLES } from '../../../../../helpers/utils/auth';
import {
  useAppDispatch,
  useTypedSelector,
} from '../../../hooks/useTypedSelector';
import styles from '../styles/TabContent.module.scss';

const { Option } = Select;

export interface TagAttachModalProps {
  visible: boolean;
  onClose: () => void;
  reportId: number;
  reportType: 'reports' | 'dashboards';
  reportName?: string;
  attachedTags: { id: number; name: string }[];
  allTags: { id: number; name: string, is_shared?: boolean }[];
  onAttach: (tagIds: number[]) => void;
  onDetach: (tagIds: number[]) => void;
  onCreateTag?: (name: string) => Promise<number>;
  activeMainTab:
    | 'overview'
    | 'dashboards'
    | 'reports'
    | 'ads'
    | 'bd'
    | 'access'
    | 'tools'
    | 'settings'
    | 'import'
    | 'suppliers'
    | 'education';
}
const SuperPermission = new Set([ ROLES.owner, ROLES.employee, ROLES.super ]);

export const TagAttachModal: React.FC<TagAttachModalProps> = ({
  visible,
  onClose,
  reportName,
  reportType,
  attachedTags,
  allTags,
  activeMainTab,
  onAttach,
  onDetach,
}) => {
  const [ searchQuery, setSearchQuery ] = useState('');
  const [ selectedTagIds, setSelectedTagIds ] = useState<number[]>(() =>
    attachedTags.map((t) => t.id)
  );
  const [ isCreateTagModalOpen, setIsCreateTagModalOpen ] = useState(false);
  const [ newTagName, setNewTagName ] = useState('');
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const currentUserRole = useTypedSelector((state) => state.currentUser.role);
  const [ isAllTag, setIsAllTag ] = useState(false);

  const allAvailableTags = [ ...allTags ];
  const dispatch = useAppDispatch();
  const canCreateTag = SuperPermission.has(currentUserRole);

  const handleChange = (newSelectedIds: number[]) => {
    setSelectedTagIds(newSelectedIds);
  };

  const handleCreateTag = async () => {
    if (!selectedSchool?.id) {
      message.error('Школа не выбрана');
      return;
    }

    const kindId = activeMainTab === 'reports' ? 1 : 2;

    try {
      await dispatch(
        createTagRequest({
          schoolId: selectedSchool.id,
          kindId,
          tagName: newTagName,
        })
      ).unwrap();

      message.success('Тег успешно создан');
      setIsCreateTagModalOpen(false);
      setNewTagName('');
    } catch (error) {
      message.error('Ошибка при создании тега');
    }
  };

  const filteredTags = allAvailableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onConfirm = () => {
    const added = selectedTagIds.filter(
      (id) => !attachedTags.map((el) => el.id).includes(id)
    );
    const removed = attachedTags
      .map((el) => el.id)
      .filter((id) => !selectedTagIds.includes(id));
    if (removed.length > 0) onDetach(removed);
    if (added.length > 0) onAttach(added);
    onClose();
  };
  return (
    <>
      <Modal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              Привязать тег к {reportType === 'reports' ? 'отчёту' : 'дашборду'}
              : {reportName}
            </span>
          </div>
        }
        open={visible}
        closable={false}
        footer={null}
        width={600}
        className={styles.customModal}
        destroyOnClose
        maskClosable
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '15px',
            gap: 10,
          }}
        >
          <Button onClick={onClose}>Закрыть</Button>
          <Tooltip
            title={!canCreateTag ? 'Недостаточно прав' : ''}
            trigger={'hover'}
            placement="top"
          >
            <Button
              onClick={() => setIsCreateTagModalOpen(true)}
              disabled={!canCreateTag}
            >
              Создать тег {!canCreateTag && <LockOutlined />}
            </Button>
          </Tooltip>
          <Button onClick={onConfirm}>Сохранить</Button>
        </div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Выберите теги"
          value={selectedTagIds}
          onChange={handleChange}
          onSearch={setSearchQuery}
          filterOption={false}
          showSearch
          tagRender={({ label, closable, onClose }) => (
            <Tag
              closable={closable}
              onClose={onClose}
              style={{ margin: 4, display: 'flex' }}
            >
              {label}
            </Tag>
          )}
        >
          {filteredTags.map((tag) => {
            const isDisable = false; //currentUserRole !== ROLES.super && !tag.is_shared;
            return (
                <Option key={tag.id} value={tag.id} disabled={isDisable}>
                  <Tooltip
                    title={isDisable ? 'Тег создан администратором' : ''}
                  >
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <span>{tag.name}</span>
                    </div>
                  </Tooltip>
                </Option>
            );
          })}
        </Select>
      </Modal>

      <Modal
        title="Создать новый тег"
        open={isCreateTagModalOpen}
        onOk={handleCreateTag}
        onCancel={() => setIsCreateTagModalOpen(false)}
        okText="Создать"
        cancelText="Отмена"
      >
        {currentUserRole === ROLES.super && (
          <span>
            <Checkbox
              checked={isAllTag}
              onClick={() => setIsAllTag((p) => !p)}
              id="allTag"
            />
            <label htmlFor="allTag"> Тег доступен всем ? </label>
          </span>
        )}
        <Input
          placeholder="Введите название тега"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onPressEnter={handleCreateTag}
        />
      </Modal>
    </>
  );
};
